import {
  getEdges,
  getPageInfo,
  getRelayArgs,
} from '@miragejs/graphql/lib/relay-pagination';
import { createServer } from 'miragejs';
import { filterRecords, getRecords } from '@miragejs/graphql/lib/orm/records';
import { unwrapType } from '@miragejs/graphql/lib/utils';
import { createGraphQLHandler } from '@miragejs/graphql';
import graphQLSchema from './schema.gql';
import { TimestampResolver } from 'graphql-scalars';
import { GraphQLError } from 'graphql';
import ENV from 'dashboard/config/environment';
import { decode } from 'js-base64';
import factories from './factories';
import fixtures from './fixtures';
import serializers from './serializers';
import defaultScenario from './scenarios/default';

const { admin, auth, core } = ENV.apiConfig.services;
const isTesting = ENV.environment === 'test';

// used to track hit counts for QR Export jobs
const JOB_HITS = new Map();

const CREATE_NEW_RECORD = 'CREATE_NEW_RECORD';
const SYNC_TO_RECORD = 'SYNC_TO_RECORD';
const DO_NOT_SYNC = 'DO_NOT_SYNC';

const YEAR_GROUPS = [
  'AA',
  'N12',
  'N1',
  'R5',
  '5',
  '10',
  '6',
  '11',
  '7',
  '12',
  '8',
  '9',
  '1',
  'N5',
];

export function resolveFilteredRelayConnection(
  obj,
  args,
  context,
  info,
  type,
  allArgs,
) {
  const { edges: edgesField } = type.getFields();
  const { type: edgeType } = unwrapType(edgesField.type);
  const { relayArgs, nonRelayArgs } = getRelayArgs(args);
  const { type: nodeType } = unwrapType(edgeType.getFields().node.type);

  const searchParams = nonRelayArgs.searchParams;
  const otherArgs = allArgs.searchParams;

  // serialize proStatus filter so Mirage knows how to filter it
  if ('emailVerified' in otherArgs) {
    searchParams.emailStatus = otherArgs.emailVerified
      ? 'VERIFIED'
      : 'UNVERIFIED';
  }

  const {
    query,
    orderBy,
    purchaserId,
    accountStatuses,
    exclude,
    yearGroup,
    schoolStatuses,
    purchaserLinkType,
  } = otherArgs;

  // these are arrays and are not handled properly by `filterRecords`
  delete searchParams.accountStatuses;
  delete searchParams.schoolStatuses;
  delete searchParams.purchaserLinkType;

  // this is also an array and is not handled properly by `filterRecords`
  if (yearGroup) delete searchParams.yearGroup;

  if (searchParams.role) {
    searchParams.userType = searchParams.role.toUpperCase();
    delete searchParams.role;
  }

  let userId = null;
  if (info.fieldName === 'courses') {
    if (otherArgs.userId) {
      /* the `courses` query, when filtering by a specific user, uses a singular `userId`
        for both teachers and students, so we can't directly convert to
        teacherId/studentId since there's no way to differentiate. filter by this ID
        afterwards. */
      userId = otherArgs.userId;
      delete searchParams.userId;
    }
  }

  let classId = null;
  let schoolId = null;
  if (
    info.fieldName === 'courseTeachers' ||
    info.fieldName === 'courseStudents'
  ) {
    // we can't directly filter by `classId` so save it for filtering afterwards
    classId = otherArgs.classId;
    delete searchParams.classId;
    schoolId = otherArgs.purchaserId;
    // purchaserId is not in CourseMemberSearchArgsInput; derive from the class
    if (!schoolId && classId) {
      schoolId = context.mirageSchema.courses.find(classId)?.purchaserId;
    }
    otherArgs.purchaserId = classId;
    delete searchParams.purchaserId;
  }

  const allRecords =
    obj && obj[info.fieldName] && obj[info.fieldName].models
      ? filterRecords(obj[info.fieldName].models, searchParams)
      : getRecords(nodeType, searchParams, context.mirageSchema);
  let records = allRecords;

  // apply account status filter
  if (accountStatuses) {
    const statuses = accountStatuses.map((x) => x.toLowerCase());

    records = records.filter((x) => {
      return statuses.includes(x.accountStatus.toLowerCase());
    });
  }

  // apply school status filter (schoolStatuses for teacher/student queries, purchaserLinkType for course member queries)
  const schoolStatusInput = schoolStatuses ?? purchaserLinkType;
  if (schoolStatusInput) {
    records = records.filter((user) => {
      const hasPurchaserName = !!user.linkedPurchaserName;
      const hasDistrict = !!user.linkedDistrictId;

      const isCurrent = user.linkedPurchaserId === schoolId && hasPurchaserName;
      const isNoSchool = !hasPurchaserName && hasDistrict;
      const isExternal = !hasPurchaserName && !hasDistrict;

      return (
        (schoolStatusInput.includes('current') && isCurrent) ||
        (schoolStatusInput.includes('no_school') && isNoSchool) ||
        (schoolStatusInput.includes('external') && isExternal)
      );
    });
  }

  // apply yearGroup filter
  if (yearGroup) {
    records = records.filter((x) => {
      return yearGroup.includes(x.yearGroup);
    });
  }

  // apply exclude filter to existing Showbie accounts in Settings > Review Matching Accounts
  if (exclude === 'new-users' || exclude === 'existing-users') {
    records = records.filter((x) => {
      return exclude === 'new-users' ? !!x.showbieId : !x.showbieId;
    });
  } else if (exclude === 'nonerrors') {
    // include only errored users - i.e. in download-errored-users
    records = records.filter((x) => !!x.errorCode);
  }

  // apply system errors filter
  if (info.fieldName === 'syncUsers') {
    if (exclude === 'nonsystemerrors') {
      // only include users with a system error
      records = records.filter((x) => !!x.systemErrorMessage);
    } else {
      // exclude any users with a system error
      records = records.filter((x) => !x.systemErrorMessage);
    }
  }

  if (info.fieldName === 'courses') {
    if (userId) {
      // filter by userId (from specific student/teacher courses screen)
      let teacherCourses = records.filter((x) => x.teacherId === userId);
      if (teacherCourses?.length > 0) {
        records = teacherCourses;
      } else {
        records = records.filter((x) => x.studentId === userId);
      }
    } else if (otherArgs.purchaserId) {
      // filter by purchaserId (from main courses screen)
      // TODO (already done? depends if we keep `purchaserId` as supplied name)
    }
  }

  const sortBy = orderBy ? orderBy.split(/,|-/)[0] : '';
  const sortDir = orderBy ? orderBy.split(/,|-/)[1] : '';

  if (sortBy === 'accountStatus') {
    const sortByLastName = (a, b) => b['lastName'].localeCompare(a['lastName']);
    const pending = [...records]
      .filter((r) => r.accountStatus === 'PENDING')
      .sort(sortByLastName);
    const synced = [...records]
      .filter((r) => r.accountStatus === 'SYNCED')
      .sort(sortByLastName);
    const deactivated = [...records]
      .filter((r) => r.accountStatus === 'DEACTIVATED')
      .sort(sortByLastName);
    const member = [...records]
      .filter((r) => r.accountStatus === 'MEMBER')
      .sort(sortByLastName);

    records = [...deactivated, ...member, ...synced, ...pending];
  } else if (sortBy === 'reviewed') {
    // for MIS account review specifically - sort by reviewed and then by name
    records = records.sort((a, b) => {
      if (b.hasBeenReviewed !== a.hasBeenReviewed) {
        return b.hasBeenReviewed ? 1 : -1;
      }
      return b.name.localeCompare(a.name);
    });
  } else if (sortBy) {
    records = records.sort((a, b) => {
      if (typeof a[sortBy] === 'number') {
        return (a[sortBy] ?? 0) - (b[sortBy] ?? 0);
      }
      if (!(sortBy in b) || !(sortBy in a)) return 0;
      return b[sortBy].localeCompare(a[sortBy]);
    });
  }

  if (sortDir === 'asc') {
    records = records.reverse();
  }

  if (query) {
    records = records.filter((item) => {
      return (
        (item.firstName &&
          item.firstName
            .toLocaleLowerCase()
            .includes(query.toLocaleLowerCase())) ||
        (item.lastName &&
          item.lastName
            .toLocaleLowerCase()
            .includes(query.toLocaleLowerCase())) ||
        item.name.toLocaleLowerCase().includes(query.toLocaleLowerCase()) ||
        (item.email &&
          item.email.toLocaleLowerCase().includes(query.toLocaleLowerCase()))
      );
    });
  }

  /* skip for courseTeachers/courseStudents — external/no-school users have no linkedPurchaserId
     and are instead scoped by the classId filter below */
  if (purchaserId && !classId) {
    const miragePurchaser = context.mirageSchema.purchasers.find(purchaserId);
    let relevantPurchaserIds = [purchaserId];

    if (miragePurchaser.type === 'District') {
      const schools = context.mirageSchema.purchasers.where({
        type: 'School',
        district: purchaserId,
      });
      relevantPurchaserIds = [purchaserId, ...schools.models.map((i) => i.id)];
    }

    records = records.filter((item) => {
      return (
        relevantPurchaserIds.includes(item.linkedPurchaserId) ||
        relevantPurchaserIds.includes(item.purchaserId)
      );
    });
  }

  if (classId) {
    // narrow down to users whose courses belong to the same school as the viewed class
    records = records.filter((user) => {
      const userCourses =
        info.fieldName === 'courseTeachers'
          ? context.mirageSchema.courses.where({ teacherId: user.id })
          : context.mirageSchema.courses.where({ studentId: user.id });

      return userCourses.models.some(
        (c) => !c.purchaserId || c.purchaserId === schoolId,
      );
    });
  }

  const edges = getEdges(records, relayArgs, nodeType.name);

  return {
    edges,
    pageInfo: getPageInfo(records, edges),
    queryResultCount: records.length,
  };
}

const _syncClassesOrGroups = function (obj, args, mirageSchema, classType) {
  const { schoolSyncId, query, orderBy } = args.searchParams;
  const sortBy = orderBy ? orderBy.split('-')[0] : '';
  const sortDir = orderBy ? orderBy.split('-')[1] : '';
  const syncDetails = mirageSchema.purchaserSyncDetails.find(schoolSyncId);
  const purchaser = mirageSchema.purchasers.findBy({
    syncDetailsId: syncDetails?.id,
  });
  const collection =
    classType === 'groups' ? mirageSchema.syncGroups : mirageSchema.syncClasses;
  let records = collection.where({
    purchaserId: purchaser?.id,
  }).models;

  // apply text search filter on name
  const trimmedQuery = (query || '').trim().toLowerCase();
  if (trimmedQuery) {
    records = records.filter((r) =>
      r.name?.toLowerCase().includes(trimmedQuery),
    );
  }

  // apply sorting
  if (sortBy === 'reviewed') {
    // for MIS class review specifically - sort by reviewed and then by name
    records = records.sort((a, b) => {
      if (b.hasBeenReviewed !== a.hasBeenReviewed) {
        return b.hasBeenReviewed ? 1 : -1;
      }
      return b.name.localeCompare(a.name);
    });
    if (sortDir === 'asc') {
      records = records.reverse();
    }
  } else if (sortBy) {
    records = records.sort((a, b) => {
      if (typeof a[sortBy] === 'number') {
        return (a[sortBy] ?? 0) - (b[sortBy] ?? 0);
      }
      if (!(sortBy in b) || !(sortBy in a)) return 0;
      return b[sortBy].localeCompare(a[sortBy]);
    });
    if (sortDir === 'asc') {
      records = records.reverse();
    }
  }

  const { relayArgs } = getRelayArgs(args);
  const edges = getEdges(records, relayArgs, 'SyncItem');

  return {
    edges,
    pageInfo: getPageInfo(records, edges),
    queryResultCount: records.length,
  };
};

const _syncErrors = function (args, mirageSchema) {
  const { schoolSyncId } = args;
  const syncDetails = mirageSchema.purchaserSyncDetails.find(schoolSyncId);
  const purchaser = mirageSchema.purchasers.findBy({
    syncDetailsId: syncDetails?.id,
  });

  const erroredUsers = mirageSchema.syncUsers
    .where({ purchaserId: purchaser?.id })
    .models.filter((user) => !!user.systemErrorMessage)
    .map((user) => ({
      id: user.id,
      recordType: user.userType,
      name: user.name,
      systemErrorMessage: user.systemErrorMessage,
    }));

  const erroredClasses = mirageSchema.syncClasses
    .where({ purchaserId: purchaser?.id })
    .models.filter((syncClass) => !!syncClass.systemErrorMessage)
    .map((syncClass) => ({
      id: syncClass.id,
      recordType: 'CLASS',
      name: syncClass.name,
      systemErrorMessage: syncClass.systemErrorMessage,
    }));

  const erroredGroups = mirageSchema.syncGroups
    .where({ purchaserId: purchaser?.id })
    .models.filter((syncGroup) => !!syncGroup.systemErrorMessage)
    .map((syncGroup) => ({
      id: syncGroup.id,
      recordType: 'GROUP',
      name: syncGroup.name,
      systemErrorMessage: syncGroup.systemErrorMessage,
    }));

  const records = [...erroredUsers, ...erroredClasses, ...erroredGroups];
  return records;
};

const updateSync = function (
  userIds,
  schoolSyncId,
  mirageSchema,
  newAction = CREATE_NEW_RECORD,
  mode,
) {
  let user, userType;
  let returnArray = [];

  if (mode === 'EXISTING' && newAction === CREATE_NEW_RECORD) {
    newAction = SYNC_TO_RECORD;
  }

  userIds.forEach((userId) => {
    user = mirageSchema.syncUsers.find(userId);
    user.update({ action: newAction });
    if (!userType) userType = user.userType;
    returnArray.push(user);
  });

  // update `totalWithCreateNewRecord` for test suite
  const purchaserSyncId =
    mirageSchema.purchaserSyncDetails.find(schoolSyncId).id;
  const purchaserId = mirageSchema.purchasers.where({
    syncDetailsId: purchaserSyncId,
  }).models[0].id;
  const purchaserSchoolStats =
    mirageSchema.schoolSyncStatistics.find(schoolSyncId);

  // always filter out users unless they match purchaserId, userType and aren't errored
  const matchingUser = (user) => {
    return (
      user.purchaserId === purchaserId &&
      user.userType === userType &&
      !user.systemErrorMessage
    );
  };

  // use base filter plus separate out by action names for generating counts
  const existingUsersToSync = mirageSchema.syncUsers.where(
    (u) => matchingUser(u) && u.action === SYNC_TO_RECORD,
  );
  const newUsersToSync = mirageSchema.syncUsers.where(
    (u) => matchingUser(u) && u.action === CREATE_NEW_RECORD,
  );
  const usersToNotSync = mirageSchema.syncUsers.where(
    (u) => matchingUser(u) && u.action === DO_NOT_SYNC,
  );

  const existingUsersToSyncCount = existingUsersToSync.filter(
    (u) => !!u.showbieId,
  ).length;
  const existingUsersToNotSyncCount = usersToNotSync.filter(
    (u) => !!u.showbieId,
  ).length;
  const newUsersToSyncCount = newUsersToSync.filter((u) => !u.showbieId).length;
  const newUsersToNotSyncCount = usersToNotSync.filter(
    (u) => !u.showbieId,
  ).length;

  if (userType === 'TEACHER') {
    purchaserSchoolStats.update({
      totalExistingTeachersWithSyncToRecord: existingUsersToSyncCount,
      totalExistingTeachersWithDoNotSync: existingUsersToNotSyncCount,
      totalNewTeachersWithCreateNewRecord: newUsersToSyncCount,
      totalNewTeachersWithDoNotSync: newUsersToNotSyncCount,
      totalTeachersSynced: 0, // just to ensure count accurate while in review
    });
  } else {
    purchaserSchoolStats.update({
      totalExistingStudentsWithSyncToRecord: existingUsersToSyncCount,
      totalExistingStudentsWithDoNotSync: existingUsersToNotSyncCount,
      totalNewStudentsWithCreateNewRecord: newUsersToSyncCount,
      totalNewStudentsWithDoNotSync: newUsersToNotSyncCount,
      totalStudentsSynced: 0, // just to ensure count accurate while in review
    });
  }
  return returnArray;
};

const updateSyncItem = function (
  itemIds,
  schoolSyncId,
  mirageSchema,
  newAction,
) {
  const returnArray = [];
  itemIds.forEach((id) => {
    const item =
      mirageSchema.syncClasses.find(id) || mirageSchema.syncGroups.find(id);
    if (item) {
      item.update({ action: newAction });
      returnArray.push(item);
    }
  });

  const purchaserSchoolStats =
    mirageSchema.schoolSyncStatistics.find(schoolSyncId);
  const purchaserId = mirageSchema.purchasers.where({
    syncDetailsId: schoolSyncId,
  }).models[0].id;

  const allClasses = mirageSchema.syncClasses.where({ purchaserId });
  const allGroups = mirageSchema.syncGroups.where({ purchaserId });

  purchaserSchoolStats.update({
    totalClassesWithCreateNewRecord: allClasses.filter(
      (c) => c.action === CREATE_NEW_RECORD,
    ).length,
    totalClassesWithDoNotSync: allClasses.filter(
      (c) => c.action === DO_NOT_SYNC,
    ).length,
    totalGroupsWithCreateNewRecord: allGroups.filter(
      (g) => g.action === CREATE_NEW_RECORD,
    ).length,
    totalGroupsWithDoNotSync: allGroups.filter((g) => g.action === DO_NOT_SYNC)
      .length,
  });

  return returnArray;
};

// @see https://github.com/bgantzler/ember-mirage/blob/main/docs/migration.md
export default function makeServer(config = {}) {
  const isTest = config.environment === 'test';

  return createServer({
    ...config,
    factories,
    serializers,
    seeds: isTest
      ? undefined
      : (server) => {
          server.db.loadData(fixtures);
          defaultScenario(server);
        },
    trackRequests: true,
    routes,
  });
}

const routes = function () {
  const graphQLHandler = createGraphQLHandler(graphQLSchema, this.schema, {
    resolvers: {
      // The service side is using Java `Long` for Unix timestamps and some
      // larger number values. I'm pretty sure these aren't actually needed
      // as all the existing values are effectively readonly for the client.
      Timestamp: TimestampResolver,

      Query: {
        user(obj, args, context) {
          const { mirageSchema } = context;

          // get session user ID from ESA storage
          const id = JSON.parse(
            localStorage.getItem('ember_simple_auth-session') ?? '{}',
          )?.authenticated?.user;

          let user = id && mirageSchema.users.find(id);
          if (!user) {
            // use the first user as a fallback
            user = mirageSchema.users.first();
          }

          return user;
        },

        teachers(obj, args, context, info) {
          const allArgs = {
            ...args,
            searchParams: { ...args.searchParams },
          };

          if (args.after === 'BAD_CURSOR' || args.before === 'BAD_CURSOR') {
            throw new GraphQLError('The cursor is invalid.', {
              extensions: {
                code: '20002',
                status: 400,
                title: 'The cursor is invalid.',
              },
            });
          }

          // deleting all properties that are not specific fields on the Teacher type
          // this allows the results to be filtered conventionally (e.g. `allTeachers.where(searchParams)`)
          delete args.searchParams.purchaserId;
          delete args.searchParams.query;
          delete args.searchParams.orderBy;
          delete args.searchParams.emailVerified;

          let { type } = unwrapType(info.returnType);

          let response = resolveFilteredRelayConnection(
            obj,
            args,
            context,
            info,
            type,
            allArgs,
          );

          return response;
        },

        students(obj, args, context, info) {
          const allArgs = {
            ...args,
            searchParams: { ...args.searchParams },
          };

          if (args.after === 'BAD_CURSOR' || args.before === 'BAD_CURSOR') {
            throw new GraphQLError(
              'There are no elements found with respect to the cursor.',
              {
                extensions: {
                  code: '20003',
                  status: 400,
                  title:
                    'There are no elements found with respect to the cursor.',
                },
              },
            );
          }

          // deleting all properties that are not specific fields on the Student type
          // this allows the results to be filtered conventionally (e.g. `allStudents.where(searchParams)`)
          delete args.searchParams.purchaserId;
          delete args.searchParams.query;
          delete args.searchParams.orderBy;
          delete args.searchParams.emailVerified;

          let { type } = unwrapType(info.returnType);

          let response = resolveFilteredRelayConnection(
            obj,
            args,
            context,
            info,
            type,
            allArgs,
          );

          return response;
        },

        courses(obj, args, context, info) {
          const allArgs = {
            ...args,
            searchParams: { ...args.searchParams },
          };

          delete args.searchParams.query;
          delete args.searchParams.orderBy;

          let { type } = unwrapType(info.returnType);

          let response = resolveFilteredRelayConnection(
            obj,
            args,
            context,
            info,
            type,
            allArgs,
          );

          return response;
        },

        courseTeachers(obj, args, context, info) {
          const allArgs = {
            ...args,
            searchParams: { ...args.searchParams },
          };

          if (args.after === 'BAD_CURSOR' || args.before === 'BAD_CURSOR') {
            throw new GraphQLError('The cursor is invalid.', {
              extensions: {
                code: '20002',
                status: 400,
                title: 'The cursor is invalid.',
              },
            });
          }

          // get purchaserId from classId
          const purchaserId = context.mirageSchema.courses.find(
            args.searchParams.classId,
          )?.purchaserId;

          args.searchParams.purchaserId = purchaserId;
          args.purchaserId = purchaserId;
          allArgs.searchParams.purchaserId = purchaserId;

          // deleting all properties that are not specific fields on the Teacher type
          // this allows the results to be filtered conventionally (e.g. `allTeachers.where(searchParams)`)
          delete args.searchParams.query;
          delete args.searchParams.orderBy;
          delete args.searchParams.emailVerified;

          let { type } = unwrapType(info.returnType);

          let response = resolveFilteredRelayConnection(
            obj,
            args,
            context,
            info,
            type,
            allArgs,
          );

          return response;
        },

        // TODO merge logic with courseTeachers
        courseStudents(obj, args, context, info) {
          const allArgs = {
            ...args,
            searchParams: { ...args.searchParams },
          };

          if (args.after === 'BAD_CURSOR' || args.before === 'BAD_CURSOR') {
            throw new GraphQLError('The cursor is invalid.', {
              extensions: {
                code: '20002',
                status: 400,
                title: 'The cursor is invalid.',
              },
            });
          }

          // get purchaserId from classId
          const purchaserId = context.mirageSchema.courses.find(
            args.searchParams.classId,
          )?.purchaserId;

          args.searchParams.purchaserId = purchaserId;
          args.purchaserId = purchaserId;
          allArgs.searchParams.purchaserId = purchaserId;

          delete args.searchParams.query;
          delete args.searchParams.orderBy;
          delete args.searchParams.emailVerified;

          let { type } = unwrapType(info.returnType);

          let response = resolveFilteredRelayConnection(
            obj,
            args,
            context,
            info,
            type,
            allArgs,
          );

          return response;
        },

        // TODO: we shouldn't have to manually configure this.
        analyticsEmbed(obj, args) {
          // return context.mirageSchema.analyticsEmbeds.create();
          const randy = Math.random();
          return {
            // using purchaserId and randy to make these URLs unique per request
            // TODO: Need to do something with this mock URL...
            url: `https://mock.looker-embed.com?with=all&kinds=of&randy=${randy}&purchaserId=${args.purchaserId}`,
          };
        },

        /**
         * Return a list of Administrators for the current purchaser.
         * The current purchaser is available via `args.id` but
         * we also have to account for the parent district for school types.
         */
        administrators(obj, args, context) {
          const { mirageSchema } = context;
          const requestedPurchaser = mirageSchema.purchasers.find(args.id);

          let relevantPurchaserIds = [args.id];

          if (requestedPurchaser.type === 'School') {
            relevantPurchaserIds.push(requestedPurchaser.district);
            // purge any null values
            relevantPurchaserIds = relevantPurchaserIds.filter(Boolean);
          }

          // find all administrators of the current school (requestedPurchaser)
          // OR the parent district; their IDs are collected in relevantPurchaserIds
          const administrators = mirageSchema.users.where((a) => {
            let intersection = a.managedPurchaserIds.filter((x) =>
              relevantPurchaserIds.includes(x),
            );
            return !!intersection.length;
          });
          return administrators.models;
        },

        purchaser(obj, args, context) {
          const { mirageSchema } = context;
          const { id } = args;

          return mirageSchema.purchasers.find(id) ?? {};
        },

        purchaserSettings(obj, args, context) {
          const { mirageSchema } = context;
          const { purchaserId } = args;

          return (
            mirageSchema.purchaserSettings.find(purchaserId) ?? {
              activationLinkEnabled: false,
              aiEnabled: false,
              allowJoinExternalClasses: false,
            }
          );
        },

        plan(obj, args, context) {
          const { mirageSchema } = context;
          const { purchaserId } = args;

          return (
            // NOTE: purchaserId is only defined in our fixtures; not on the service.
            // Using `.findBy` here specifically to allow us find the appropriate plan for the current purchaser.
            mirageSchema.plans.findBy({ purchaserId }) ?? {
              id: 'BASIC',
              name: 'Free',
              features: [],
            }
          );
        },

        schoolSyncStats(obj, args, context) {
          const { mirageSchema } = context;
          const { schoolSyncId } = args;

          return mirageSchema.schoolSyncStatistics.find(schoolSyncId) ?? {};
        },

        schoolSyncProgress(obj, args, context) {
          const { mirageSchema } = context;
          const { schoolSyncId } = args;

          return (
            mirageSchema.schoolSyncProgresses.find(schoolSyncId) ?? {
              progress: null,
            }
          );
        },

        syncUsers(obj, args, context, info) {
          const allArgs = {
            ...args,
            searchParams: { ...args.searchParams },
          };

          // deleting all properties that are not specific fields on the Teacher type
          // this allows the results to be filtered conventionally (e.g. `allTeachers.where(searchParams)`)
          delete args.searchParams.purchaserId;
          delete args.searchParams.query;
          delete args.searchParams.orderBy;
          delete args.searchParams.exclude;

          // use `purchaserId` instead of `syncId` in search
          const { schoolSyncId } = args.searchParams;
          delete args.searchParams.schoolSyncId;
          const syncDetails = context.mirageSchema.purchaserSyncDetails.where({
            id: schoolSyncId,
          });
          const purchaser = context.mirageSchema.purchasers.where({
            syncDetailsId: syncDetails.models[0].id,
          });
          args.searchParams.purchaserId = purchaser.models[0].id;

          let { type } = unwrapType(info.returnType);

          let response = resolveFilteredRelayConnection(
            obj,
            args,
            context,
            info,
            type,
            allArgs,
          );

          return response;
        },

        signInQRExportJobStatus(obj, args, context) {
          const { mirageSchema } = context;
          const { jobId } = args;

          const job = mirageSchema.signInQrExportJobStatuses.findBy({ jobId });

          if (!job) {
            throw new GraphQLError('signInQrExportJobStatuses not found', {
              extensions: {
                code: 'XXXXX',
                status: 400,
                title:
                  'signInQrExportJobStatuses not found (check Mirage config)',
              },
            });
          }

          const hits = JOB_HITS.get(jobId);

          if (hits) {
            JOB_HITS.set(jobId, hits + 1);
          } else {
            JOB_HITS.set(jobId, 1);
          }

          // set job status to completed after a few rounds of polling
          if (hits > 2) {
            job.status = 'C';
            job.reportUrl = 'https://example.test/path/to/file.pdf';
            job.save();
          }

          return job;
        },

        misHandout(obj, args) {
          const { purchaserId } = args;
          if (purchaserId === '10003') {
            // City College
            return 'https://example.test/path/to/file.pdf';
          }
          return '';
        },

        schoolSyncYearGroups(/* obj, args */) {
          // TODO this can use similar logic as below
          return { yearGroups: YEAR_GROUPS };
        },

        purchaserYearGroups(obj, args, context) {
          // compile all year groups from all students in the specified school
          const { mirageSchema } = context;
          const { purchaserId } = args;

          const students = mirageSchema.students.where({
            linkedPurchaserId: purchaserId,
          });

          let yearGroups = new Set();
          students.models.forEach((student) => {
            if (student.yearGroup) {
              yearGroups.add(student.yearGroup);
            }
          });

          return [...yearGroups];
        },

        course(obj, args, context) {
          const { id } = args;
          const course = context.mirageSchema.courses.find(id);

          if (!course) {
            throw new GraphQLError('Class not found', {
              extensions: {
                code: 'XXXXX', // TODO
                status: 400,
                title: 'Class not found',
              },
            });
          }
          return course ?? {};
        },

        syncClasses(obj, args, { mirageSchema }) {
          return _syncClassesOrGroups(obj, args, mirageSchema, 'classes');
        },

        syncGroups(obj, args, { mirageSchema }) {
          return _syncClassesOrGroups(obj, args, mirageSchema, 'groups');
        },

        syncErrors(obj, args, { mirageSchema }) {
          return _syncErrors(args, mirageSchema);
        },
      },
      Mutation: {
        teacherBulkAction(obj, args, context) {
          const purchaser = context.mirageSchema.purchasers.find(
            args.purchaserId,
          );
          const teachers = context.mirageSchema.teachers.find(args.teacherIds);

          return {
            purchaser: purchaser,
            teachers: teachers,
          };
        },
        assignCoursesToTeacher(obj, args, { mirageSchema }) {
          const { teacherId, courseCodes } = args;
          const teacher = mirageSchema.teachers.find(teacherId);
          let data = [];
          courseCodes.forEach((code) => {
            // hardcoded generic error for testing
            if (code === '__MOCK_COURSE_CODE_ERROR') {
              throw new GraphQLError(
                'The class has teachers that does not belong to your school. Please contact support.',
                {
                  extensions: {
                    code: '19010',
                    status: 400,
                    message:
                      'The class has teachers that does not belong to your school. Please contact support.',
                  },
                },
              );
            }

            const course = mirageSchema.courses.findBy({ code });

            if (course) {
              if (course.teacher?.id === teacher?.id) {
                data.push({
                  __typename: 'IdentifiableError',
                  identifier: code,
                  identifierType: 'classCode',
                  message: 'You already belong to this class',
                  code: '4002',
                  status: '400',
                });
              } else {
                course.teacher = teacher;
                course.save(); // persist the teacher change for the course
                data.push({
                  __typename: 'AssignCourseToTeacherResultSuccess',
                  course,
                });
              }
            } else {
              data.push({
                __typename: 'IdentifiableError',
                identifier: code,
                identifierType: 'classCode',
                message: "This isn't a valid class code.",
                code: '4001',
                status: '400',
              });
            }
          });
          return data;
        },
        resendActivationEmails(obj, args) {
          // , { mirageSchema }
          const { ids } = args;
          return ids.map((/*id*/) => {
            // const teacher = mirageSchema.teachers.find(id);
            // if (!teacher)
            //   return {
            //     __typename: 'IdentifiableError',
            //     identifier: id,
            //     identifierType: 'teacher',
            //     message: "This teacher doesn't exist.",
            //     // code: '4001',
            //     status: '400',
            //   };
          });
        },
        connectSyncSchool(obj, args, { mirageSchema }) {
          let { active, integratorId, integratorTypeId, purchaserId } =
            args.school;
          const requestedPurchaser = mirageSchema.purchasers.find(purchaserId);

          const syncSchool = mirageSchema.purchaserSyncDetails.create({
            id: integratorId,
            active,
            name: integratorTypeId,
          });
          requestedPurchaser.update({ syncDetailsId: syncSchool.id });

          return {
            syncSchool: {
              integratorId,
              integratorTypeId,
              purchaserId,
            },
          };
        },
        disconnectSyncSchool(obj, args, { mirageSchema }) {
          let { schoolSyncId: syncId } = args;
          const syncDetails = mirageSchema.purchaserSyncDetails.find(syncId);
          /*
          const purchaser = mirageSchema.purchasers.findBy({
            syncDetailsId: syncDetails.id,
          });
          */

          syncDetails.update({ active: false });
          return { result: 'SUCCESS' };
        },

        disableSyncUser(obj, args, { mirageSchema }) {
          let users = updateSync(
            args.userIds,
            args.schoolSyncId,
            mirageSchema,
            DO_NOT_SYNC,
          );
          return users;
        },

        enableSyncUser(obj, args, { mirageSchema }) {
          let users = updateSync(
            args.userIds,
            args.schoolSyncId,
            mirageSchema,
            CREATE_NEW_RECORD,
            args.mode,
          );
          return users;
        },

        disableSyncClass(obj, args, { mirageSchema }) {
          return updateSyncItem(
            args.classIds,
            args.schoolSyncId,
            mirageSchema,
            DO_NOT_SYNC,
          );
        },

        disableSyncGroup(obj, args, { mirageSchema }) {
          return updateSyncItem(
            args.groupIds,
            args.schoolSyncId,
            mirageSchema,
            DO_NOT_SYNC,
          );
        },

        enableSyncClass(obj, args, { mirageSchema }) {
          return updateSyncItem(
            args.classIds,
            args.schoolSyncId,
            mirageSchema,
            CREATE_NEW_RECORD,
          );
        },

        enableSyncGroup(obj, args, { mirageSchema }) {
          return updateSyncItem(
            args.groupIds,
            args.schoolSyncId,
            mirageSchema,
            CREATE_NEW_RECORD,
          );
        },

        startSchoolSync(obj, args, { mirageSchema }) {
          const okResult = { result: 'ok' };
          const { schoolSyncId } = args.startSchoolSyncParams;

          // always succeeds except for Hogwarts (sync ID of 7)
          if (schoolSyncId !== '7') {
            // only City College will show any progress
            if (schoolSyncId !== '2') return okResult;

            let ssProgress = mirageSchema.schoolSyncProgresses.findBy({
              id: schoolSyncId,
            });
            let ssStats = mirageSchema.schoolSyncStatistics.findBy({
              id: schoolSyncId,
            });
            ssProgress.update({ progress: 0, triggerMode: 'USER' });

            setTimeout(
              () => {
                ssProgress.update({ progress: 50 });
              },
              isTesting ? 500 : 5000,
            );

            setTimeout(
              () => {
                ssProgress.update({ progress: 100 });
                ssStats.update({
                  totalNewTeachersNotReviewed: 0,
                  totalNewTeachersWithCreateNewRecord: 0,
                  totalNewTeachersWithDoNotSync: 0,
                  totalExistingTeachersWithSyncToRecord: 0,
                  totalExistingTeachersWithDoNotSync: 0,
                });
              },
              isTesting ? 1000 : 15000,
            );

            return okResult;
          }

          /* Hogwarts will error with currently running unless there are users
             with the same email set to CREATE_NEW_RECORD */

          let purchaser = mirageSchema.purchasers.findBy({
            syncDetailsId: schoolSyncId,
          });
          let users = mirageSchema.syncUsers.where({
            purchaserId: purchaser.id,
          });
          let createUsers = users.models.filter((user) => {
            return user.action === CREATE_NEW_RECORD;
          });

          let emailCount = createUsers.reduce((acc, user) => {
            acc[user.email] = (acc[user.email] || 0) + 1;
            return acc;
          }, {});

          let hasDuplicateEmails = Object.values(emailCount).some(
            (count) => count > 1,
          );

          if (hasDuplicateEmails) {
            throw new GraphQLError(
              'Exception while fetching data (/startSchoolSync) : Duplicate emails found',
              {
                extensions: {
                  code: '1001',
                  title: 'Duplicate emails found',
                  status: 400,
                  classification: 'DataFetchingException',
                },
              },
            );
          }

          throw new GraphQLError(
            'Exception while fetching data (/startSchoolSync) : Sync session currently running.',
            {
              extensions: {
                code: '1000',
                title: 'Sync session currently running',
                status: 400,
                classification: 'DataFetchingException',
              },
            },
          );
        },

        markSyncRecordsAsReviewed(
          obj,
          { schoolSyncId, syncType },
          { mirageSchema },
        ) {
          // use `purchaserId` instead of `schoolSyncId`
          const syncDetails = mirageSchema.purchaserSyncDetails.where({
            id: schoolSyncId,
          });
          const purchaser = mirageSchema.purchasers.where({
            syncDetailsId: syncDetails.models[0].id,
          });
          const purchaserId = purchaser.models[0].id;

          if (syncType === 'CLASS') {
            const classes = mirageSchema.syncClasses.where({ purchaserId });
            classes.models.forEach((cls) => {
              cls.update({ hasBeenReviewed: true });
            });
            const groups = mirageSchema.syncGroups.where({ purchaserId });
            groups.models.forEach((group) => {
              group.update({ hasBeenReviewed: true });
            });
          } else {
            const users = mirageSchema.syncUsers.where({
              purchaserId,
              userType: syncType,
            });
            users.models.forEach((user) => {
              user.update({ hasBeenReviewed: true });
            });
          }
        },

        updateStudent(obj, args, { mirageSchema }) {
          const { id, firstName, lastName, email } = args.student;
          const student = mirageSchema.students.find(id);
          student.update({ firstName, lastName, email });

          return {
            id,
            firstName,
            lastName,
            email,
          };
        },

        setStudentPassword(obj, args, { mirageSchema }) {
          let { studentId } = args;
          const student = mirageSchema.students.find(studentId);
          return student;
        },

        setSSOStudentPassword(obj, args, { mirageSchema }) {
          let { studentId, username, password } = args;

          if (username === '_FORCED_USERNAME_ERROR') {
            throw new GraphQLError('This username is already taken.', {
              extensions: {
                code: '2012',
                status: 400,
                title: 'The username is already taken.',
              },
            });
          } else if (password === '_FORCED_PASSWORD_ERROR') {
            throw new GraphQLError(
              'Password must be less than 128 characters long.',
              {
                extensions: {
                  code: '2073',
                  status: 400,
                  title: 'Password must be less than 128 characters long.',
                },
              },
            );
          }

          const student = mirageSchema.students.find(studentId);
          return student;
        },

        updatePurchaserSettings(obj, args, { mirageSchema }) {
          const settingObj = mirageSchema.purchaserSettings.find(
            args.purchaserId,
          );

          if (settingObj && args.settings) {
            settingObj.update({ ...args.settings });
            return settingObj;
          }

          throw new GraphQLError('PurchaserSettings not found', {
            extensions: {
              code: 'XXXXX',
              status: 400,
              title: 'PurchaserSettings not found (check Mirage config)',
            },
          });
        },

        startSignInQRExportJob(obj, args, { mirageSchema }) {
          const existingJob = mirageSchema.signInQrExportJobStatuses.find(
            args.purchaserId,
          );

          return existingJob
            ? existingJob
            : mirageSchema.signInQrExportJobStatuses.create({
                status: 'A',
                totalStudents: 42,
              });
        },

        removeStudentAvatar(obj, args, { mirageSchema }) {
          const student = mirageSchema.students.find(args.studentId);
          student.update({ avatarSmallUrl: '', avatarMediumUrl: '' });

          return { id: args.studentId };
        },

        deactivate(obj, args, { mirageSchema }) {
          let user;
          args.ids.forEach((id) => {
            user = mirageSchema.teachers.find(id);
            if (!user) user = mirageSchema.students.find(id);

            if (user) user.update({ accountStatus: 'DEACTIVATED' });
          });
        },

        activate(obj, args, { mirageSchema }) {
          let user;
          args.ids.forEach((id) => {
            user = mirageSchema.teachers.find(id);
            if (!user) user = mirageSchema.students.find(id);

            if (user) user.update({ accountStatus: 'MEMBER' });
          });
        },

        deleteUser(obj, args, { mirageSchema }) {
          const { clientId } = args;
          let user = mirageSchema.teachers.find(clientId);
          if (!user) user = mirageSchema.students.find(clientId);

          if (user) {
            user.update({
              linkedPurchaserName: null,
              linkedPurchaserId: null,
              accountStatus: 'DEACTIVATED',
            });
          }
        },

        unsyncUser(obj, args, { mirageSchema }) {
          const { clientId } = args;
          let user = mirageSchema.teachers.find(clientId);
          if (!user) user = mirageSchema.students.find(clientId);

          if (user) user.update({ accountStatus: 'MEMBER', managed: false });
        },
      },
    },
  });

  // https://adminqa.showbie.com/adminservice/v1/graphql
  const graphqlUrl = `${admin.origin}/${admin.namespace}/graphql`;
  this.post(graphqlUrl, graphQLHandler);

  const authUrl = `${auth.origin}/${auth.namespace}`;
  // https://adminqa.showbie.com/auth/v1/region
  this.post(`${authUrl}/region`, function (_, request) {
    const body = JSON.parse(request.requestBody);
    const code = body.contextType === 'regionCode' ? body.context : 'us-east-1';
    return { origin: admin.origin, code };
  });

  const coreUrl = `${core.origin}/${core.namespace}`;

  // https://adminqa.showbie.com/core/v5/sessions
  this.post(`${coreUrl}/sessions`, function ({ users }, request) {
    /**
     * Detect if username matches a supported locale, otherwise simple load
     * the main fixture. This allows us to sign-in as different locales in dev
     * and testing mode.
     */
    const auth = request.requestHeaders.Authorization;
    // decode the Basic auth header, from base64 to a string
    const [username] = decode(auth.split(' ')[1]).split(':');
    const user = users.findBy({ locale: username });
    const userId = user?.id || users.first().id;

    return {
      session: {
        user: userId,
        token: 'mock_mirage_access_token',
      },
    };
  });

  this.post(`${coreUrl}/sessions/oauth`, function ({ users }, request) {
    const { provider } = JSON.parse(request.requestBody);
    return {
      session: {
        user: users.first().id,
        token: 'mock_mirage_access_token',
        provider,
      },
    };
  });
  this.get(`${coreUrl}/sessions/oidc/logout`, function () {
    console.debug(`${coreUrl}/sessions/oidc/logout`);
    return {};
  });
  this.delete(`${coreUrl}/sessions/:id`, function () {
    return;
  });

  /**
   * This is required for the "system" icon library in Shoelace which uses
   * embedded data URL SVGs to ensure availability and don't load properly
   * through Mirage without this direct Pretender handler.
   *
   * @see https://stackoverflow.com/a/66935216/1874753
   * @see https://github.com/pretenderjs/pretender?tab=readme-ov-file#pass-through
   */
  this.pretender.get('data:image/*', this.pretender.passthrough);

  /**
   * Needed because Chrome recognizes that the Mirage Response is not a real response
   * with setting instantiateStreaming to null we fallback to legacy WebAssembly instantiation
   * this works with the Mirage Response, therefore the app can start
   * for more details see: https://github.com/miragejs/miragejs/issues/339
   *
   * This avoids the followin error when using ember-shiki:
   * ```
   * An argument must be provided, which must be a Response or Promise<Response> object
   * ```
   */
  Object.defineProperty(window.WebAssembly, 'instantiateStreaming', {
    value: null,
  });
  const oldPassthroughRequests = this.pretender.passthroughRequest.bind(
    this.pretender,
  );
  this.pretender.passthroughRequest = (verb, path, request) => {
    // Needed because responseType is not set correctly in Mirages passthrough
    // for more details see: https://github.com/miragejs/ember-cli-mirage/issues/1915
    if (verb === 'GET' && path.match(/\.wasm$/)) {
      console.log('Set responseType for wasm correctly');
      request.responseType = 'arraybuffer';
    }
    return oldPassthroughRequests(verb, path, request);
  };

  /**
   * Allow unhandled requests on the current domain to pass through.
   * This is to ensure Shoelace icons work properly but if we need to
   * change this for any reason we can target them directly:
   *
   * For Shoelace assets (icons); see shoelace.js and webpack config:
   * this.passthrough('/shoelace/**');
   *
   * For custom "showbie" icon library for Shoelace <sl-icon>:
   * this.passthrough('/assets/**');
   *
   * This also applies to Shiki (code formatting used in LBX docs) and WASM.
   */
  this.passthrough();

  this.passthrough('https://fast.fonts.net/**');
  this.passthrough('https://login.microsoftonline.com/**');
  this.passthrough('https://auth.dataporten.no/**');
  this.passthrough('https://et-broker.unilogin.dk/**');
  this.passthrough('https://accounts.google.com/**');
  this.passthrough('https://api.amplitude.com');
  this.passthrough('https://api2.amplitude.com/2/httpapi');
  this.passthrough('https://api.typeform.com/**');
  this.passthrough('https://cdn.jsdelivr.net/**'); // https://cdn.jsdelivr.net/npm/shiki
  this.passthrough('/write-coverage');
};
